import { apiService } from "../../../utils/api/api.service";
import { requestWithErrorHandling } from "../../../utils/error/handle.error";
import { TokenManager } from "../../../utils/storage/tokenmanager.storage";
import { gcpStorageService } from "../../../services/gcpStorageService";
import {
  CreateProductData,
  UpdateProductData,
  ProductFilters,
  ProductSearchParams,
} from "../interfaces/products.interface";

class ProductsService {
  private static instance: ProductsService;

  static getInstance(): ProductsService {
    if (!ProductsService.instance) {
      ProductsService.instance = new ProductsService();
    }
    return ProductsService.instance;
  }

  // Get all products with optional filters
  getProducts = (params?: ProductFilters) => {
    return requestWithErrorHandling(async () => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            queryParams.append(key, value.toString());
          }
        });
      }
      return await apiService.get<any>(`/products?${queryParams.toString()}`);
    });
  };

  // Get single product by ID
  getProduct = (id: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<any>(`/products/${id}`);
    });
  };

  // Get single product by slug (for product detail page)
  getProductBySlug = (slug: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<any>(`/products/slug/${encodeURIComponent(slug)}`);
    });
  };

  // Get product reviews
  getProductReviews = (productId: string, currentUserId?: string) => {
    return requestWithErrorHandling(async () => {
      const params: any = {};
      if (currentUserId) {
        params.currentUserId = currentUserId;
      }
      return await apiService.get<any>(`/products/${productId}/reviews`, { params });
    });
  };

  // Post product review
  postProductReview = (
    productId: string,
    payload: { rating: number; comment?: string }
  ) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>(`/products/${productId}/reviews`, payload);
    });
  };

  // Create new product (full validation; for publishing)
  createProduct = (productData: CreateProductData) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>("/products", productData);
    });
  };

  // Create or save draft (title only required)
  createDraft = (draftData: Partial<CreateProductData> & { title: string }) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>("/products/drafts", draftData);
    });
  };

  // Update product (full validation; for publishing)
  updateProduct = (id: string, productData: UpdateProductData) => {
    return requestWithErrorHandling(async () => {
      return await apiService.put<any>(`/products/${id}`, productData);
    });
  };

  // Update draft (relaxed validation; allows price 0, empty category, etc.)
  updateDraft = (id: string, draftData: Partial<UpdateProductData> & { title: string }) => {
    return requestWithErrorHandling(async () => {
      return await apiService.put<any>(`/products/${id}/draft`, draftData);
    });
  };

  // Update product status
  updateProductStatus = (id: string, status: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.patch<any>(`/products/${id}/status`, { status });
    });
  };

  // Delete product
  deleteProduct = (id: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.delete<any>(`/products/${id}`);
    });
  };

  // Get user's products
  getUserProducts = (userId?: string, query?: Record<string, string>) => {
    return requestWithErrorHandling(async () => {
      let endpoint = userId
        ? `/products/user/${userId}`
        : "/products/my-products";

      if (query) {
        const queryParams = new URLSearchParams(query);
        endpoint += `?${queryParams.toString()}`;
      }

      return await apiService.get<any>(endpoint);
    });
  };

  // Get my products with pagination and optional status filter
  getMyProducts = (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    return requestWithErrorHandling(async () => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== "") {
            queryParams.append(key, value.toString());
          }
        });
      }
      const query = queryParams.toString();
      return await apiService.get<any>(
        `/products/my-products${query ? `?${query}` : ""}`
      );
    });
  };

  // Search products
  searchProducts = (
    query: string,
    filters?: ProductSearchParams["filters"],
    page: number = 1,
    limit: number = 100,
  ) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>("/products/search", {
        query,
        filters,
        page,
        limit,
      });
    });
  };

  // Get categories
  getCategories = () => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<any>("/categories");
    });
  };

  // Get locations
  getLocations = () => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<any>("/locations");
    });
  };

  /**
   * Submit or update review helpfulness vote (like/dislike).
   * One vote per user per review:
   * - Send true to vote "helpful"
   * - Send false to vote "not helpful"
   * - Sending the same value again will toggle the vote off(handled by backend).
   */
  submitReviewHelpfulness = (reviewId: string, isHelpful: boolean) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>(
        `/products/reviews/${reviewId}/helpfulness`,
        { isHelpful }
      );
    });
  };

  // Get helpfulness counts for a review (also returns user's vote if authenticated)
  getReviewHelpfulnessCounts = (reviewId: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.get<any>(`/products/reviews/${reviewId}/helpfulness`);
    });
  };

  // Upload product image (via backend proxy to GCP to avoid CORS)
  uploadProductImage = (productId: string, file: File) => {
    return requestWithErrorHandling(async () => {
      const userId = TokenManager.getUser()?.id ?? "";
      const { viewUrl, key } = await gcpStorageService.uploadProductImageViaBackend(
        file,
        userId
      );
      return await apiService.post<any>(`/products/${productId}/images`, {
        images: [
          {
            url: viewUrl,
            key,
            isPrimary: false,
            order: 0,
          },
        ],
      });
    });
  };

  // Track product view
  trackProductView = (productId: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>(`/products/${productId}/view`, {});
    });
  };
}

export const productsService = ProductsService.getInstance();
